import { useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import { DesignSystemRecipe } from 'designSystem/types';
import { resolveTokenValue } from 'designSystem/tokens/resolver';
import * as S from './styles';

interface RecipeSpecimensProps {
	previewText: string;
	dark: boolean;
}

// Resolves a recipe's CSS properties into an inline style object
const buildSpecimenStyle = (recipe: DesignSystemRecipe): React.CSSProperties => {
	const style: Record<string, string> = {};
	for (const prop of recipe.properties) {
		const resolved = resolveTokenValue(prop.value);
		const camelCase = prop.property.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
		style[camelCase] = resolved;
	}
	return style as React.CSSProperties;
};

const getFontSizePx = (recipe: DesignSystemRecipe): number => {
	const sizeProp = recipe.properties.find((p) => p.property === 'font-size');
	if (!sizeProp) return 16;
	const resolved = resolveTokenValue(sizeProp.value);
	const match = resolved.match(/([\d.]+)px/);
	if (match) return parseFloat(match[1]);
	const remMatch = resolved.match(/([\d.]+)rem/);
	if (remMatch) return parseFloat(remMatch[1]) * 16;
	return 16;
};

const buildTooltipText = (recipe: DesignSystemRecipe): string => {
	const lines = [recipe.className];
	for (const prop of recipe.properties) {
		lines.push(`${prop.property}: ${prop.value}`);
	}
	return lines.join('\n');
};

const RecipeSpecimens = ({ previewText, dark }: RecipeSpecimensProps) => {
	const dispatch = useDispatch();
	const { typeRecipes, selectedPicks } = useSelector(
		(r: RootReducer) => ({
			typeRecipes: r.mode.designSystemContent?.styles?.recipes?.byDomain['type'] ?? [],
			selectedPicks: r.mode.selectedDsPicks,
		}),
		shallowEqual
	);

	const sorted = useMemo(
		() => [...typeRecipes].sort((a, b) => getFontSizePx(b) - getFontSizePx(a)),
		[typeRecipes]
	);

	const isSelected = (recipe: DesignSystemRecipe) =>
		selectedPicks.some((p) => p.kind === 'recipe' && p.className === recipe.className);

	if (sorted.length === 0) return null;

	return (
		<S.SpecimenList $dark={dark}>
			{sorted.map((recipe) => {
				const style = buildSpecimenStyle(recipe);
				return (
					<S.SpecimenRow
						key={recipe.id}
						type="button"
						$selected={isSelected(recipe)}
						$dark={dark}
						onClick={() =>
							dispatch(toggleDsPick({ kind: 'recipe', className: recipe.className }))
						}
					>
						<S.SpecimenTooltip>{buildTooltipText(recipe)}</S.SpecimenTooltip>
						<S.SpecimenLabel>{recipe.name}</S.SpecimenLabel>
						<S.SpecimenText style={{ ...style, color: 'inherit' }}>{previewText}</S.SpecimenText>
					</S.SpecimenRow>
				);
			})}
		</S.SpecimenList>
	);
};

export default RecipeSpecimens;
